/**
 * What a playlist looks like: its own cover if it has one, otherwise the
 * sleeves of what is in it.
 *
 * ## Four sleeves in a square
 *
 * A playlist is made of other people's records and their covers say what
 * it is far better than a striped placeholder; every player draws one this
 * way. Four in a two-by-two grid when there are four; the first alone when
 * there are fewer, because a grid with holes reads as broken rather than as
 * short; the stripes only when nothing in it has art.
 *
 * The server chooses the four (`CollectionRepository::mosaics`): in playlist
 * order, skipping tracks with no art and tracks the reader cannot see.
 */
export function PlaylistCover({
  artwork,
  mosaic,
  className = '',
  rounded = 'rounded-[var(--radius-card)]',
}: {
  artwork: string | null;
  mosaic?: string[];
  className?: string;
  rounded?: string;
}) {
  const tiles = mosaic ?? [];

  if (artwork) {
    return (
      <img src={artwork} alt="" loading="lazy" className={`${rounded} object-cover ${className}`} data-playlist-cover="own" />
    );
  }

  if (tiles.length >= 4) {
    return (
      <div className={`grid grid-cols-2 grid-rows-2 overflow-hidden ${rounded} ${className}`} data-playlist-cover="mosaic">
        {tiles.slice(0, 4).map((src, i) => (
          <img key={`${src}-${i}`} src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
        ))}
      </div>
    );
  }

  if (tiles.length > 0) {
    return (
      <img src={tiles[0]} alt="" loading="lazy" className={`${rounded} object-cover ${className}`} data-playlist-cover="first" />
    );
  }

  return (
    <div
      aria-hidden
      className={`${rounded} ${className}`}
      data-playlist-cover="empty"
      style={{
        backgroundImage:
          'repeating-linear-gradient(45deg, var(--color-blank) 0 10px, var(--color-blank-alt) 10px 20px)',
      }}
    />
  );
}
